import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import EntitiesService from "../../services/EntitiesService";
import UsersService from "../../services/UsersService";
import StudentsGroupService from "../../services/StudentsGroupService";
import IEntityData from "../../types/Entities";
import IUsersData from "../../types/Users";
import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/event_form_banner.png";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useDefaultStyles } from "../../common/styles/default";
import SearchBar from "../../common/components/search/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import secureLocalStorage from 'react-secure-storage'

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

export default function FormStudentsGroupV2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const styles = useDefaultStyles();
  const { id } = useParams<{ id: string }>();

  const [idGroup, setIdGroup] = useState<number | undefined>(id ? parseInt(id, 10) : undefined);
  const [entities, setEntities] = useState<IEntityData[]>([]);
  const [name, setName] = useState("");
  const [studentsgroup, setStudentsGroup] = useState("");
  const [students, setStudents] = useState<IUsersData[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [isGroupSaving, setIsGroupSaving] = useState(false);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);
  const [isLinkingStudents, setIsLinkingStudents] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStudents, setFilteredStudents] = useState<IUsersData[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const roleId = secureLocalStorage.getItem('role')

  const fetchStudents = useCallback((entityId?: string) => {
    setIsStudentsLoading(true);
    UsersService.getStudentsV2(roleId, entityId || '')
      .then((response: any) => {
        const studentList = response?.data || [];
        setStudents(studentList);
        setFilteredStudents(studentList);
        setPage(0);
      })
      .catch(() => toast(t("toast_msg_formstudentsgroup_users"), { type: "error" }))
      .finally(() => setIsStudentsLoading(false));
  }, [roleId, t]);

  // Carregar entidades e grupo/alunos se edição
  useEffect(() => {
    setInitialLoading(true);
    EntitiesService.getAll(1, 0)
      .then((response: any) => setEntities(response?.data || []))
      .catch(() => toast(t("toast_msg_formestudentsgroup_entity"), { type: "error" }))
      .finally(() => setInitialLoading(false));

    if (idGroup) {
      StudentsGroupService.get(idGroup)
        .then((response: any) => {
          setIdGroup(response.data.id);
          setName(response.data.name);
          setStudentsGroup(response.data.entity_id);
          // Alunos já vinculados
          const arrStudents = response?.data?.students?.map((student: { id: number }) => student.id) || [];
          setSelectedStudents(arrStudents);
          fetchStudents(response.data.entity_id);
        })
        .catch(() => toast(t("toast_msg_formstudentsgroup_load"), { type: "error" }));
    } else {
      fetchStudents();
    }
  }, [idGroup, t, fetchStudents]);

  const handleStudentsGroup = (event: SelectChangeEvent) => {
    const selectedId = event.target.value;
    setStudentsGroup(selectedId);
    fetchStudents(selectedId);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (name === "" || studentsgroup === "") {
      toast(
        t(
          name === ""
            ? "toast_msg_formstudentsgroup_name"
            : "toast_msg_formestudentsgroup_entity"
        ),
        { type: "error" }
      );
      return;
    }
    setIsGroupSaving(true);
    const dataStudentsGroup = {
      id: "",
      name: name,
      entity_id: studentsgroup,
      parent_id: "",
    };
    if (!idGroup) {
      StudentsGroupService.store(dataStudentsGroup)
        .then((response: any) => {
          setIdGroup(response.data.id);
          toast(t("toast_msg_formestudentsgroup_save"), { type: "success" });
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error || t("toast_msg_formestudentsgroup_entity"), { type: "error" });
        })
        .finally(() => setIsGroupSaving(false));
    } else {
      StudentsGroupService.update(idGroup, dataStudentsGroup)
        .then(() => {
          toast(t("toast_msg_formestudentsgroup_save"), { type: "success" });
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error || t("toast_msg_formestudentsgroup_entity"), { type: "error" });
        })
        .finally(() => setIsGroupSaving(false));
    }
  };

  const handleStudentSelect = (id: number) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleAddStudents = () => {
    if (selectedStudents.length === 0 || !idGroup) {
      toast(t("toast_msg_formstudentsgroup_users"), { type: "error" });
      return;
    }
    setIsLinkingStudents(true);
    const dataStudents = {
      id: idGroup,
      users: selectedStudents,
    };
    StudentsGroupService.linkStudents(dataStudents)
      .then(() => {
        toast(t("toast_msg_formestudentsgroup_save"), { type: "success" });
        navigate("/studentsgroup/list");
      })
      .catch((e: any) => {
        toast(e?.response?.data?.Error || t("toast_msg_formstudentsgroup_users"), { type: "error" });
      })
      .finally(() => setIsLinkingStudents(false));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const normalize = (s: string) =>
    s
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, '')

  // Accepts payload from SearchBar: string | { q: string, filters?: Record }
  const handleSearch = (payload: any) => {
    try {
      let q = "";
      let filters: Record<string, any> | undefined = undefined;

      if (!payload) {
        setSearchQuery("");
        setFilteredStudents(students);
        setPage(0);
        return;
      }

      if (typeof payload === "string") {
        q = payload;
      } else if (typeof payload === "object") {
        q = payload.q ?? "";
        filters = payload.filters;
      }

      setSearchQuery(q);

      const normalized = normalize(String(q || ""));

      const result = students.filter((s) => {
        // apply filters if present
        if (filters) {
          for (const k of Object.keys(filters)) {
            const fv = filters[k];
            // simple equality check (coerce to string)
            if (fv !== undefined && fv !== null && String(s[k]) !== String(fv)) {
              return false;
            }
          }
        }

        if (!normalized) return true;

        // search fields
        const name = normalize(String(s.name || ""));
        const email = normalize(String(s.email || ""));
        const cpf = normalize(String(s.cpf || ""));
        const ra = s.ra ? normalize(String(s.ra)) : "";

        return (
          name.includes(normalized) ||
          email.includes(normalized) ||
          cpf.includes(normalized) ||
          ra.includes(normalized)
        );
      });

      setFilteredStudents(result);
      setPage(0);
    } catch (e) {
      // fallback: no filter
      setFilteredStudents(students);
      setPage(0);
    }
  };

  return (
    <Box>
      <Banner
        type="small"
        image={banner}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <GroupsOutlinedIcon sx={{ width: "3em", height: "3em" }} />
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {!id ? t("title_page_form_register_studentsgroup") : t("title_page_form_register_studentsgroup_edit")}
            </Typography>
          </Box>
        }
      />
      <Box className={styles.container}>
        {initialLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography sx={{ color: 'primary.main' }}>{t('loading')}</Typography>
          </Box>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ mb: 2, width: "50%" }}>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              label={t("input_label_studentsgroup")}
              variant="outlined"
              value={name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setName(event.target.value);
              }}
            />
          </Box>
          <Box sx={{ mb: 2, width: "50%" }}>
            <FormControl fullWidth>
              <InputLabel id="studentsgroup-label">{t("input_label_entity")}</InputLabel>
              <Select
                labelId="studentsgroup-label"
                label={t("input_label_entity")}
                value={studentsgroup}
                onChange={handleStudentsGroup}
              >
                {entities.map((row, index) => (
                  <MenuItem key={index} value={row.id}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isGroupSaving}
            startIcon={isGroupSaving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {!idGroup ? t("btn_create_class") : t("btn_salvar_class")}
          </Button>
          <Button
            variant="contained"
            size="large"
            color="secondary"
            sx={{ ml: 2 }}
            disabled={isGroupSaving || isLinkingStudents}
            onClick={() => {
              navigate("/studentsgroup/list");
            }}
          >
            {t("btn_return")}
          </Button>
        </Box>
        {/* Listagem de usuários com busca e seleção */}
        {idGroup !== undefined && (
          <Box sx={{ mt: 4, width: "100%" }}>
            <Typography variant="h5" sx={{ mb: 2 }}>{t("title_component_form_classes_students")}</Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              {/* Coluna maior: listagem de usuários */}
              <Box sx={{ flex: 3 }}>
                <SearchBar onSearch={handleSearch} />
                <TableContainer component={Paper} sx={{ mt: 2, position: 'relative' }}>
                  {isStudentsLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <Table sx={{ minWidth: 600 }} aria-label="users table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_name")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_email")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_document1")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_document2")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_registration")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_label_profile")}</TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>{t("table_btn_select")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStudents.length === 0 && !isStudentsLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            {t("no_records_found", "Nenhum aluno encontrado")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredStudents
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((student) => (
                            <TableRow key={student.id} hover>
                              <TableCell>{student.name}</TableCell>
                              <TableCell>{student.email}</TableCell>
                              <TableCell>{student.cpf}</TableCell>
                              <TableCell>{student.rg}</TableCell>
                              <TableCell>{student.ra}</TableCell>
                              <TableCell>{t(student.roles?.[0]?.name)}</TableCell>
                              <TableCell>
                                <Checkbox
                                  color="primary"
                                  checked={selectedStudents.includes(student.id)}
                                  onChange={() => handleStudentSelect(student.id)}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                    {filteredStudents.length > 0 && (
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            colSpan={7}
                            count={filteredStudents.length}
                            rowsPerPage={rowsPerPage}
                            labelRowsPerPage={t("table_label_number_per_page", "Linhas por página:")}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                          />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
                <Button
                  variant="contained"
                  size="large"
                  color="secondary"
                  sx={{ mt: 2 }}
                  disabled={isLinkingStudents || isStudentsLoading}
                  startIcon={isLinkingStudents ? <CircularProgress size={20} color="inherit" /> : null}
                  onClick={handleAddStudents}
                >
                  {t("btn_add_students_class")}
                </Button>
              </Box>
              {/* Coluna menor: usuários selecionados */}
              <Box sx={{ flex: 1, bgcolor: '#f8fafc', borderRadius: 2, p: 2, minWidth: 260, maxWidth: 340, height: 'fit-content' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('selected_users', 'Usuários selecionados')}</Typography>
                {students.filter(s => selectedStudents.includes(s.id)).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('no_users_selected', 'Nenhum usuário selecionado')}</Typography>
                ) : (
                  students.filter(s => selectedStudents.includes(s.id)).map((student) => (
                    <Box key={student.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1, bgcolor: '#fff', borderRadius: 1, boxShadow: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 500 }}>{student.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{student.email}</Typography>
                      </Box>
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        disabled={isLinkingStudents}
                        onClick={() => handleStudentSelect(student.id)}
                      >
                        {t('remove', 'Remover')}
                      </Button>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
